import { forgetAnonymousUpload, getAnonymousUploadClaim, rememberAnonymousUpload } from "./anonymous-upload-claims";

// A bounded, tab-local handoff from presigning to task creation. These upload
// capabilities must never be copied into deck metadata or diagnostic context.
const uploadCapabilities = new Map<string, { proof: Record<string, string>; claim: string; createdAt: number }>();
const UPLOAD_CAPABILITY_LIFETIME_MS = 60 * 60 * 1000;

function getUnexpiredUploadCapability(objectKey: string) {
  const capability = uploadCapabilities.get(objectKey);
  return capability && Date.now() - capability.createdAt < UPLOAD_CAPABILITY_LIFETIME_MS ? capability : undefined;
}

/**
 * The private signup claim sent with this object's processing task. Only the landing page reads it: its upload
 * happens on jungleai.com, whose storage the app cannot read, so the claim must travel with the handoff or the
 * backend refuses to generate from the visitor's own anonymous upload.
 */
export function getUploadClaimTokenForObjectKey(objectKey: string): string | undefined {
  return getUnexpiredUploadCapability(objectKey)?.claim;
}
import {
  LOCAL_FILE_READ_PROBE_MAX_BYTES,
  LOCAL_FILE_READ_PROBE_TIMEOUT_MS,
  LOCAL_FILE_MATERIALIZE_TIMEOUT_MS,
  type LocalFileReadFailureKind,
  type LocalFileReadabilityResult,
  type ReadBlobBytes,
  materializeLocalFile,
  probeLocalFileReadability,
  shouldMaterializeUploadBodyAfterFirstTransportFailure,
} from "../../utils/local-file-readability";

export enum FileUploadStage {
  ValidateInput = "validate-input",
  ReadLocalFile = "read-local-file",
  RequestUploadUrl = "request-upload-url",
  UploadToStorage = "upload-to-storage",
  CreateProcessingTask = "create-processing-task",
  AwaitProcessing = "await-processing",
  ReadProcessedText = "read-processed-text",
  RetrieveSource = "retrieve-source",
}

export const FILE_PROCESSING_RETRY_GRACE_PERIOD_MS = 30_000;
// The exact S3 missing-file-part rejection also affected a 101 MB PDF. Keep
// this recovery bounded separately from the smaller fast-transport-failure cap.
const S3_FILE_PART_RECOVERY_MAX_BYTES = 128 * 1024 * 1024;
// The API's Cloudflare zone accepts request bodies up to 100 MB. Leave room
// for multipart fields/boundaries; larger files must stay on direct S3.
const CLOUDFLARE_PROXY_MAX_FILE_BYTES = 99_000_000;

export enum FileUploadErrorCode {
  Cancelled = "cancelled",
  EmptyFile = "empty-file",
  TooManyFiles = "too-many-files",
  UnsupportedFile = "unsupported-file",
  FilePreparation = "file-preparation",
  FileUnreadable = "file-unreadable",
  Network = "network",
  RequestTimeout = "request-timeout",
  Http = "http",
  InvalidResponse = "invalid-response",
  Listener = "listener",
  MissingTask = "missing-task",
  SourceUnavailable = "source-unavailable",
  ProcessingTimeout = "processing-timeout",
  RestorationRequired = "restoration-required",
  ProcessingFailed = "processing-failed",
}

export type FileUploadInputMethod = "file-picker" | "drag-and-drop" | "query-parameter" | "unknown";
export type FileUploadTranslate = (
  key: string,
  fallback: string,
  parameters?: Record<string, string | number>
) => string;
export type StorageUploadTransport = "fetch" | "xhr" | "cloudflare-proxy";

export interface StorageUploadProgress {
  loadedBytes: number;
  totalBytes: number;
  progressPercent: number;
}

const useFallbackTranslation: FileUploadTranslate = (_key, fallback) => fallback;

interface FileUploadErrorOptions {
  message: string;
  stage: FileUploadStage;
  code: FileUploadErrorCode;
  retryable: boolean;
  requestAttempts?: number;
  httpStatus?: number;
  cfRayId?: string;
  storageRequestId?: string;
  storageExtendedRequestId?: string;
  storageTransport?: StorageUploadTransport;
  storageErrorCode?: string;
  storageErrorMessage?: string;
  responseContentType?: string;
  responseBodyType?: string;
  responseBodyKeys?: string[];
  fileReadFailureKind?: LocalFileReadFailureKind;
  /** True when the failed request carried an in-memory copy of the picked file. */
  bodyMaterialized?: boolean;
  cause?: unknown;
}

export class FileUploadError extends Error {
  readonly stage: FileUploadStage;
  readonly code: FileUploadErrorCode;
  readonly retryable: boolean;
  readonly requestAttempts: number;
  readonly httpStatus?: number;
  readonly cfRayId?: string;
  readonly storageRequestId?: string;
  readonly storageExtendedRequestId?: string;
  readonly storageTransport?: StorageUploadTransport;
  readonly storageErrorCode?: string;
  readonly storageErrorMessage?: string;
  readonly responseContentType?: string;
  readonly responseBodyType?: string;
  readonly responseBodyKeys?: string[];
  readonly fileReadFailureKind?: LocalFileReadFailureKind;
  readonly bodyMaterialized: boolean;
  readonly cause?: unknown;

  constructor({
    message,
    stage,
    code,
    retryable,
    requestAttempts = 1,
    httpStatus,
    cfRayId,
    storageRequestId,
    storageExtendedRequestId,
    storageTransport,
    storageErrorCode,
    storageErrorMessage,
    responseContentType,
    responseBodyType,
    responseBodyKeys,
    fileReadFailureKind,
    bodyMaterialized = false,
    cause,
  }: FileUploadErrorOptions) {
    super(message);
    this.name = "FileUploadError";
    this.stage = stage;
    this.code = code;
    this.retryable = retryable;
    this.requestAttempts = requestAttempts;
    this.httpStatus = httpStatus;
    this.cfRayId = cfRayId;
    this.storageRequestId = storageRequestId;
    this.storageExtendedRequestId = storageExtendedRequestId;
    this.storageTransport = storageTransport;
    this.storageErrorCode = storageErrorCode;
    this.storageErrorMessage = storageErrorMessage;
    this.responseContentType = responseContentType;
    this.responseBodyType = responseBodyType;
    this.responseBodyKeys = responseBodyKeys;
    this.fileReadFailureKind = fileReadFailureKind;
    this.bodyMaterialized = bodyMaterialized;
    this.cause = cause;
  }
}

export function cloneFileUploadError(error: FileUploadError, overrides: Partial<FileUploadErrorOptions>): FileUploadError {
  return new FileUploadError({
    message: error.message,
    stage: error.stage,
    code: error.code,
    retryable: error.retryable,
    requestAttempts: error.requestAttempts,
    httpStatus: error.httpStatus,
    cfRayId: error.cfRayId,
    storageRequestId: error.storageRequestId,
    storageExtendedRequestId: error.storageExtendedRequestId,
    storageTransport: error.storageTransport,
    storageErrorCode: error.storageErrorCode,
    storageErrorMessage: error.storageErrorMessage,
    responseContentType: error.responseContentType,
    responseBodyType: error.responseBodyType,
    responseBodyKeys: error.responseBodyKeys,
    fileReadFailureKind: error.fileReadFailureKind,
    bodyMaterialized: error.bodyMaterialized,
    cause: error.cause,
    ...overrides,
  });
}

export interface PresignedPostPayload {
  url: string;
  fields: Record<string, string>;
}

export interface StableUploadedFileIdentifiers {
  bucketName?: string | null;
  objectKey?: string | null;
  taskId?: string | null;
  modalFunctionCallId?: string | null;
}

const LOCAL_UPLOAD_SOURCE_HOSTNAME = "uploaded-file.invalid";
const uploadedFileDownloadUrlCache = new Map<string, { url: string; expiresAt: number }>();
let uploadedFileDownloadUrlCacheOwnerId: string | null = null;

function scopeUploadedFileDownloadUrlCache(ownerUserId: string | null | undefined): void {
  if (!ownerUserId) {
    // Firebase can briefly report no current user while restoring a session.
    // A read during that window must not erase the confirmed owner's cache.
    return;
  }
  if (uploadedFileDownloadUrlCacheOwnerId && uploadedFileDownloadUrlCacheOwnerId !== ownerUserId) {
    uploadedFileDownloadUrlCache.clear();
  }
  uploadedFileDownloadUrlCacheOwnerId = ownerUserId;
}

export function buildLocalUploadPageUrl(taskId: string, fileName: string): string {
  const extensionMatch = fileName.toLowerCase().match(/\.([a-z0-9]{1,10})$/);
  const sourceFileName = extensionMatch ? `source.${extensionMatch[1]}` : "source";
  return `https://${LOCAL_UPLOAD_SOURCE_HOSTNAME}/${encodeURIComponent(taskId)}/${sourceFileName}`;
}

export function isLocalUploadPageUrl(pageUrl: string | null | undefined): boolean {
  if (!pageUrl) return false;
  try {
    return new URL(pageUrl).hostname === LOCAL_UPLOAD_SOURCE_HOSTNAME;
  } catch {
    return false;
  }
}

export function getLocalUploadTaskId(pageUrl: string | null | undefined): string | null {
  if (!pageUrl) return null;
  try {
    const parsedUrl = new URL(pageUrl);
    if (parsedUrl.hostname !== LOCAL_UPLOAD_SOURCE_HOSTNAME) return null;
    const encodedTaskId = parsedUrl.pathname.split("/").filter(Boolean)[0];
    return encodedTaskId ? decodeURIComponent(encodedTaskId) : null;
  } catch {
    return null;
  }
}

export function isAmazonS3Url(sourceUrl: string | null | undefined): boolean {
  if (!sourceUrl) return false;
  try {
    const parsedUrl = new URL(sourceUrl);
    if (parsedUrl.protocol !== "https:" || !parsedUrl.hostname.endsWith(".amazonaws.com")) return false;
    return parsedUrl.hostname
      .split(".")
      .some((hostnameLabel) => hostnameLabel === "s3" || hostnameLabel.startsWith("s3-"));
  } catch {
    return false;
  }
}

export function getImageUrlForTypeValidation(sourceImageUrl: string, resolvedImageUrl: string): string {
  // The local marker preserves the validated upload extension. Signed storage
  // URLs are retrieval details and may not expose a usable extension or HEAD.
  return isLocalUploadPageUrl(sourceImageUrl) ? sourceImageUrl : resolvedImageUrl;
}

export function isImageSourceReadyForRendering(
  sourceImageUrl: string,
  resolvedImageUrl: string,
  didValidateExternalImageUrl: boolean
): boolean {
  return isLocalUploadPageUrl(sourceImageUrl) ? Boolean(resolvedImageUrl) : didValidateExternalImageUrl;
}

function isHttpsUrl(url: unknown): url is string {
  if (typeof url !== "string" || !url) return false;
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.protocol === "https:";
  } catch {
    return false;
  }
}

export function getCachedUploadedFileDownloadUrl(
  taskId: string | null | undefined,
  ownerUserId: string | null | undefined
): string | null {
  return getUsableUploadedFileDownloadUrlCacheEntry(taskId, ownerUserId)?.url ?? null;
}

export function getCachedUploadedFileDownloadUrlExpiresAt(
  taskId: string | null | undefined,
  ownerUserId: string | null | undefined
): number | null {
  return getUsableUploadedFileDownloadUrlCacheEntry(taskId, ownerUserId)?.expiresAt ?? null;
}

export function invalidateCachedUploadedFileDownloadUrl(
  taskId: string | null | undefined,
  ownerUserId: string | null | undefined
): void {
  if (!taskId || !ownerUserId) return;
  scopeUploadedFileDownloadUrlCache(ownerUserId);
  uploadedFileDownloadUrlCache.delete(taskId);
}

function getUsableUploadedFileDownloadUrlCacheEntry(
  taskId: string | null | undefined,
  ownerUserId: string | null | undefined
): { url: string; expiresAt: number } | null {
  if (!taskId || !ownerUserId) return null;
  scopeUploadedFileDownloadUrlCache(ownerUserId);
  const cachedDownload = uploadedFileDownloadUrlCache.get(taskId);
  if (!cachedDownload) return null;
  if (cachedDownload.expiresAt <= Date.now()) {
    uploadedFileDownloadUrlCache.delete(taskId);
    return null;
  }
  return cachedDownload;
}

export function createUploadAttemptId(): string {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }
  const randomBytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(randomBytes);
  randomBytes[6] = (randomBytes[6] & 0x0f) | 0x40;
  randomBytes[8] = (randomBytes[8] & 0x3f) | 0x80;
  const hexadecimalBytes = Array.from(randomBytes, (byte) => byte.toString(16).padStart(2, "0"));
  return [
    hexadecimalBytes.slice(0, 4).join(""),
    hexadecimalBytes.slice(4, 6).join(""),
    hexadecimalBytes.slice(6, 8).join(""),
    hexadecimalBytes.slice(8, 10).join(""),
    hexadecimalBytes.slice(10, 16).join(""),
  ].join("-");
}

export function getPresignedPostBucketName(presignedPost: PresignedPostPayload): string | null {
  try {
    const parsedUrl = new URL(presignedPost.url);
    const virtualHostedBucketMatch = parsedUrl.hostname.match(/^(.+?)\.s3(?:[.-][^.]+)?\.amazonaws\.com$/i);
    if (virtualHostedBucketMatch?.[1]) return virtualHostedBucketMatch[1];

    const pathStyleBucketName = parsedUrl.pathname.split("/").filter(Boolean)[0];
    return pathStyleBucketName || null;
  } catch {
    return null;
  }
}

type FetchImplementation = typeof fetch;
type SleepImplementation = (milliseconds: number) => Promise<void>;
export type FileRequestRetry = {
  error: unknown;
  failedAttemptNumber: number;
  maximumAttempts: number;
  retryReason?: "transport" | "response";
};

const defaultSleep: SleepImplementation = (milliseconds) =>
  new Promise((resolve) => {
    setTimeout(resolve, milliseconds);
  });

function isTransientNetworkError(error: unknown): boolean {
  if (error instanceof TypeError) {
    return true;
  }
  if (error instanceof Error || (typeof error === "object" && error !== null && "name" in error)) {
    const errorName = String((error as { name?: unknown }).name || "");
    return errorName === "NetworkError" || errorName === "AbortError";
  }
  return false;
}

export function createFileUploadCancellationError(stage: FileUploadStage, cause?: unknown): FileUploadError {
  return new FileUploadError({
    message: "File upload was cancelled",
    stage,
    code: FileUploadErrorCode.Cancelled,
    retryable: true,
    cause,
  });
}

export function isFileUploadCancellation(error: unknown): boolean {
  return error instanceof FileUploadError && error.code === FileUploadErrorCode.Cancelled;
}

export function createFileUploadPageExitMonitor({
  onPageExit,
  cancellationSignal,
  eventTarget = typeof window === "undefined" ? null : window,
}: {
  onPageExit: () => void;
  cancellationSignal?: AbortSignal;
  eventTarget?: Pick<EventTarget, "addEventListener" | "removeEventListener"> | null;
}): { markSettled: () => void; dispose: () => void } {
  let hasSettled = false;
  let hasReportedPageExit = false;
  const handlePageExit = (event: Event) => {
    const isBackForwardCacheTransition = "persisted" in event && Boolean((event as PageTransitionEvent).persisted);
    if (hasSettled || hasReportedPageExit || cancellationSignal?.aborted || isBackForwardCacheTransition) return;
    hasReportedPageExit = true;
    onPageExit();
  };

  eventTarget?.addEventListener("pagehide", handlePageExit);
  return {
    markSettled: () => {
      hasSettled = true;
    },
    dispose: () => {
      hasSettled = true;
      eventTarget?.removeEventListener("pagehide", handlePageExit);
    },
  };
}

function getResponseHeader(response: Response, headerName: string): string | undefined {
  try {
    return response.headers?.get(headerName) || undefined;
  } catch {
    return undefined;
  }
}

function getResponseBodyShape(responseBody: unknown): {
  responseBodyType: string;
  responseBodyKeys?: string[];
} {
  if (responseBody === null) return { responseBodyType: "empty" };
  if (Array.isArray(responseBody)) return { responseBodyType: "array" };
  if (typeof responseBody === "object") {
    return {
      responseBodyType: "object",
      responseBodyKeys: Object.keys(responseBody as Record<string, unknown>)
        .sort()
        .slice(0, 20),
    };
  }
  return { responseBodyType: typeof responseBody };
}

async function parseResponseBody(response: Response): Promise<unknown> {
  try {
    const responseText = await response.text();
    if (!responseText) return null;
    try {
      return JSON.parse(responseText);
    } catch {
      return responseText;
    }
  } catch {
    return null;
  }
}

async function fetchWithNetworkRetry({
  url,
  requestInit,
  stage,
  fetchImpl,
  sleepMs,
  maxAttempts,
  requestTimeoutMs,
  overallDeadlineMs,
  cancellationSignal,
  onNetworkRetry,
}: {
  url: string;
  requestInit: RequestInit | (() => RequestInit);
  stage: FileUploadStage;
  fetchImpl: FetchImplementation;
  sleepMs: SleepImplementation;
  maxAttempts: number;
  requestTimeoutMs: number;
  overallDeadlineMs?: number;
  cancellationSignal?: AbortSignal;
  onNetworkRetry?: (retry: FileRequestRetry) => void;
}): Promise<Response> {
  let lastNetworkError: unknown;
  let didLastAttemptTimeOut = false;
  let requestAttempts = 0;

  for (let attemptNumber = 1; attemptNumber <= maxAttempts; attemptNumber += 1) {
    if (cancellationSignal?.aborted) {
      throw createFileUploadCancellationError(stage, cancellationSignal.reason);
    }
    const remainingOverallTimeMs = overallDeadlineMs === undefined ? requestTimeoutMs : overallDeadlineMs - Date.now();
    if (remainingOverallTimeMs <= 0) {
      didLastAttemptTimeOut = true;
      break;
    }

    requestAttempts = attemptNumber;
    const abortController = new AbortController();
    const cancelCurrentRequest = () => abortController.abort(cancellationSignal?.reason);
    cancellationSignal?.addEventListener("abort", cancelCurrentRequest, { once: true });
    const attemptTimeoutMs = Math.min(requestTimeoutMs, remainingOverallTimeMs);
    const timeoutId = globalThis.setTimeout(() => abortController.abort(), attemptTimeoutMs);
    try {
      const requestInitForAttempt = typeof requestInit === "function" ? requestInit() : requestInit;
      return await fetchImpl(url, { ...requestInitForAttempt, signal: abortController.signal });
    } catch (error) {
      if (cancellationSignal?.aborted) {
        throw createFileUploadCancellationError(stage, cancellationSignal.reason || error);
      }
      if (!isTransientNetworkError(error)) {
        throw error;
      }
      lastNetworkError = error;
      didLastAttemptTimeOut = abortController.signal.aborted;
      const retryDelayMs = 400 * attemptNumber;
      const timeRemainingBeforeRetryMs =
        overallDeadlineMs === undefined ? retryDelayMs : overallDeadlineMs - Date.now();
      if (attemptNumber < maxAttempts && timeRemainingBeforeRetryMs > 0) {
        try {
          onNetworkRetry?.({ error, failedAttemptNumber: attemptNumber, maximumAttempts: maxAttempts });
        } catch {
          // Diagnostics must never prevent the recovery request.
        }
        await sleepMs(Math.min(retryDelayMs, timeRemainingBeforeRetryMs));
      }
    } finally {
      globalThis.clearTimeout(timeoutId);
      cancellationSignal?.removeEventListener("abort", cancelCurrentRequest);
    }
  }

  const networkMessage = didLastAttemptTimeOut
    ? "Network request timed out"
    : lastNetworkError instanceof Error
      ? lastNetworkError.message
      : "Network request failed";
  throw new FileUploadError({
    message: networkMessage,
    stage,
    code: didLastAttemptTimeOut ? FileUploadErrorCode.RequestTimeout : FileUploadErrorCode.Network,
    retryable: true,
    requestAttempts,
    cause: lastNetworkError,
  });
}

function validatePresignedPostPayload(payload: unknown): PresignedPostPayload {
  if (!payload || typeof payload !== "object") {
    throw new FileUploadError({
      message: "Upload URL response was not an object",
      stage: FileUploadStage.RequestUploadUrl,
      code: FileUploadErrorCode.InvalidResponse,
      retryable: true,
    });
  }

  const candidatePayload = payload as Record<string, unknown>;
  const fields = candidatePayload.fields;
  const key = fields && typeof fields === "object" ? (fields as Record<string, unknown>).key : null;
  if (
    typeof candidatePayload.url !== "string" ||
    !candidatePayload.url ||
    !fields ||
    typeof fields !== "object" ||
    typeof key !== "string" ||
    !key
  ) {
    throw new FileUploadError({
      message: "Upload URL response is missing a URL, fields, or object key",
      stage: FileUploadStage.RequestUploadUrl,
      code: FileUploadErrorCode.InvalidResponse,
      retryable: true,
    });
  }

  return candidatePayload as unknown as PresignedPostPayload;
}

export async function requestFileUploadUrl({
  url,
  body,
  fetchImpl = fetch,
  sleepMs = defaultSleep,
  maxAttempts = 3,
  requestTimeoutMs = 30_000,
  cancellationSignal,
}: {
  url: string;
  body: Record<string, unknown>;
  fetchImpl?: FetchImplementation;
  sleepMs?: SleepImplementation;
  maxAttempts?: number;
  requestTimeoutMs?: number;
  cancellationSignal?: AbortSignal;
}): Promise<PresignedPostPayload> {
  const normalizedMaximumAttempts = Math.max(1, maxAttempts);
  let lastError: FileUploadError | null = null;
  for (let attemptNumber = 1; attemptNumber <= normalizedMaximumAttempts; attemptNumber += 1) {
    try {
      const response = await fetchWithNetworkRetry({
        url,
        requestInit: {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
        stage: FileUploadStage.RequestUploadUrl,
        fetchImpl,
        sleepMs,
        maxAttempts: 1,
        requestTimeoutMs,
        cancellationSignal,
      });
      const responseBody = await parseResponseBody(response);
      if (!response.ok) {
        const retryable = response.status === 408 || response.status === 429 || response.status >= 500;
        throw new FileUploadError({
          message: "The upload service rejected the request",
          stage: FileUploadStage.RequestUploadUrl,
          code: FileUploadErrorCode.Http,
          retryable,
          requestAttempts: attemptNumber,
          httpStatus: response.status,
          cfRayId: getResponseHeader(response, "cf-ray"),
        });
      }

      try {
        const post = validatePresignedPostPayload(responseBody);
        if (post.fields.policy && post.fields["x-amz-signature"]) {
          const claim = Array.from(crypto.getRandomValues(new Uint8Array(32)), byte => byte.toString(16).padStart(2, "0")).join("");
          uploadCapabilities.set(post.fields.key, {
            proof: { policy: post.fields.policy, "x-amz-signature": post.fields["x-amz-signature"] },
            claim, createdAt: Date.now(),
          });
          if (uploadCapabilities.size > 20) uploadCapabilities.delete(uploadCapabilities.keys().next().value);
        }
        return post;
      } catch (error) {
        if (!(error instanceof FileUploadError)) throw error;
        throw new FileUploadError({
          message: error.message,
          stage: error.stage,
          code: error.code,
          retryable: error.retryable,
          requestAttempts: attemptNumber,
          cause: error,
        });
      }
    } catch (error) {
      if (isFileUploadCancellation(error)) throw error;
      if (!(error instanceof FileUploadError)) throw error;
      lastError =
        error.requestAttempts === attemptNumber
          ? error
          : new FileUploadError({
              message: error.message,
              stage: error.stage,
              code: error.code,
              retryable: error.retryable,
              requestAttempts: attemptNumber,
              httpStatus: error.httpStatus,
              cfRayId: error.cfRayId,
              cause: error.cause,
            });
      if (!lastError.retryable || attemptNumber >= normalizedMaximumAttempts) {
        throw lastError;
      }
      await sleepMs(400 * attemptNumber);
    }
  }

  throw (
    lastError ||
    new FileUploadError({
      message: "The upload service did not return a response",
      stage: FileUploadStage.RequestUploadUrl,
      code: FileUploadErrorCode.InvalidResponse,
      retryable: true,
      requestAttempts: normalizedMaximumAttempts,
    })
  );
}

function buildPresignedPostFormData(file: Blob, presignedPost: PresignedPostPayload): FormData {
  const postData = new FormData();
  for (const [fieldName, fieldValue] of Object.entries(presignedPost.fields)) {
    postData.append(fieldName, fieldValue);
  }
  postData.append("file", file);
  return postData;
}

export interface StorageUploadResponse {
  ok: boolean;
  status: number;
  transport: StorageUploadTransport;
  requestAttempts?: number;
  storageRequestId?: string;
  storageExtendedRequestId?: string;
  storageErrorCode?: string;
  storageErrorMessage?: string;
}

export type StorageUploadFallback = (options: {
  file: Blob;
  presignedPost: PresignedPostPayload;
  requestTimeoutMs: number;
  /** A direct request making byte progress may use this larger, still bounded budget. */
  progressingRequestTimeoutMs?: number;
  /** Tracks sent bytes even when the browser cannot compute a percentage. */
  onUploadBytesSent?: (loadedBytes: number) => void;
  requestAttemptsBeforeFallback: number;
  cancellationSignal?: AbortSignal;
  onUploadProgress?: (progress: StorageUploadProgress) => void;
  stallTimeoutMs?: number;
}) => Promise<StorageUploadResponse>;

export interface StorageTransportRecovery {
  transport: StorageUploadTransport;
  requestAttemptsBeforeFallback: number;
  storageRequestId?: string;
  storageExtendedRequestId?: string;
  /** True when the successful request carried an in-memory copy of the picked file. */
  bodyMaterialized: boolean;
}

/**
 * The readability probe can pass while the storage POST still fails at send
 * time: Chromium rejects a picked File whose document provider now reports a
 * different size or modification time. Copying the bytes into memory gives the
 * transport a body it can always stream. A failed copy is the unreadable-file
 * outcome, reported against the transport that exposed it.
 */
async function materializeUploadBody({
  file,
  failedTransportError,
  storageRequestAttempts,
  cancellationSignal,
  timeoutMs,
}: {
  file: Blob;
  failedTransportError: FileUploadError;
  storageRequestAttempts: number;
  cancellationSignal?: AbortSignal;
  timeoutMs?: number;
}): Promise<Blob> {
  const materialization = await materializeLocalFile({ file, signal: cancellationSignal, timeoutMs });
  if (
    cancellationSignal?.aborted ||
    (materialization.isMaterialized === false && materialization.failureKind === "aborted")
  ) {
    throw createFileUploadCancellationError(FileUploadStage.UploadToStorage, cancellationSignal?.reason);
  }
  if (materialization.isMaterialized === false) {
    throw new FileUploadError({
      message: "The selected file could not be read into memory after the storage transport failed",
      stage: FileUploadStage.ReadLocalFile,
      code: FileUploadErrorCode.FileUnreadable,
      retryable: false,
      requestAttempts: storageRequestAttempts,
      storageTransport: failedTransportError.storageTransport,
      fileReadFailureKind: materialization.failureKind,
      bodyMaterialized: false,
      cause: materialization.cause,
    });
  }
  return materialization.blob;
}

function getStorageErrorBodyField(responseBody: string, fieldName: string): string | undefined {
  const jsonBody = (() => {
    try {
      return JSON.parse(responseBody) as Record<string, unknown>;
    } catch {
      return null;
    }
  })();
  const jsonValue = jsonBody?.[fieldName] ?? jsonBody?.[fieldName.toLowerCase()];
  if (typeof jsonValue === "string" && jsonValue.trim()) {
    return jsonValue.trim().slice(0, 500);
  }

  const xmlMatch = responseBody.match(new RegExp(`<${fieldName}>([\\s\\S]*?)</${fieldName}>`, "i"));
  return xmlMatch?.[1]?.trim().slice(0, 500) || undefined;
}

function parseStorageErrorBody(responseBody: string): {
  storageErrorCode?: string;
  storageErrorMessage?: string;
  storageRequestId?: string;
  storageExtendedRequestId?: string;
} {
  if (!responseBody.trim()) return {};
  return {
    storageErrorCode: getStorageErrorBodyField(responseBody, "Code"),
    storageErrorMessage:
      getStorageErrorBodyField(responseBody, "Message") ||
      (responseBody.includes("<") ? undefined : responseBody.trim().slice(0, 500)),
    storageRequestId: getStorageErrorBodyField(responseBody, "RequestId"),
    storageExtendedRequestId: getStorageErrorBodyField(responseBody, "HostId"),
  };
}

async function getStorageErrorDetailsFromResponse(
  response: Response
): Promise<ReturnType<typeof parseStorageErrorBody>> {
  if (response.ok) return {};
  try {
    return parseStorageErrorBody(await response.text());
  } catch {
    return {};
  }
}

async function uploadFileToPresignedPostWithXhr({
  file,
  presignedPost,
  requestTimeoutMs,
  progressingRequestTimeoutMs = requestTimeoutMs,
  onUploadBytesSent,
  requestAttemptsBeforeFallback,
  cancellationSignal,
  onUploadProgress,
  stallTimeoutMs = 30_000,
}: {
  file: Blob;
  presignedPost: PresignedPostPayload;
  requestTimeoutMs: number;
  progressingRequestTimeoutMs?: number;
  /** Tracks sent bytes even when the browser cannot compute a percentage. */
  onUploadBytesSent?: (loadedBytes: number) => void;
  requestAttemptsBeforeFallback: number;
  cancellationSignal?: AbortSignal;
  onUploadProgress?: (progress: StorageUploadProgress) => void;
  stallTimeoutMs?: number;
}): Promise<StorageUploadResponse> {
  return await new Promise<StorageUploadResponse>((resolve, reject) => {
    if (cancellationSignal?.aborted) {
      reject(createFileUploadCancellationError(FileUploadStage.UploadToStorage, cancellationSignal.reason));
      return;
    }

    const request = new XMLHttpRequest();
    request.open("POST", presignedPost.url, true);
    request.timeout = requestTimeoutMs;
    let hasSettled = false;
    let didStall = false;
    let uploadedBytes = 0;
    let stallTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const clearRequestListeners = () => {
      if (stallTimeoutId) globalThis.clearTimeout(stallTimeoutId);
      cancellationSignal?.removeEventListener("abort", cancelRequest);
    };
    const settle = (callback: () => void) => {
      if (hasSettled) return;
      hasSettled = true;
      clearRequestListeners();
      callback();
    };
    const rejectTransportFailure = (code: FileUploadErrorCode, cause?: unknown) => {
      settle(() =>
        reject(
          new FileUploadError({
            message: code === FileUploadErrorCode.RequestTimeout ? "Storage upload timed out" : "Storage upload failed",
            stage: FileUploadStage.UploadToStorage,
            code,
            retryable: true,
            requestAttempts: requestAttemptsBeforeFallback + 1,
            storageTransport: "xhr",
            cause,
          })
        )
      );
    };
    const resetStallTimeout = () => {
      if (stallTimeoutId) globalThis.clearTimeout(stallTimeoutId);
      stallTimeoutId = globalThis.setTimeout(() => {
        didStall = true;
        rejectTransportFailure(FileUploadErrorCode.RequestTimeout);
        request.abort();
      }, Math.min(stallTimeoutMs, request.timeout));
    };
    function cancelRequest() {
      settle(() =>
        reject(createFileUploadCancellationError(FileUploadStage.UploadToStorage, cancellationSignal?.reason))
      );
      request.abort();
    }

    const buildResponse = (): StorageUploadResponse => {
      const responseBodyDetails = parseStorageErrorBody(request.responseText || "");
      return {
        ok: request.status >= 200 && request.status < 300,
        status: request.status,
        transport: "xhr",
        requestAttempts: requestAttemptsBeforeFallback + 1,
        ...responseBodyDetails,
        storageRequestId: request.getResponseHeader("x-amz-request-id") || responseBodyDetails.storageRequestId,
        storageExtendedRequestId:
          request.getResponseHeader("x-amz-id-2") || responseBodyDetails.storageExtendedRequestId,
      };
    };
    request.onload = () => {
      if (request.status === 0) {
        rejectTransportFailure(FileUploadErrorCode.Network);
        return;
      }
      settle(() => resolve(buildResponse()));
    };
    request.onerror = (event) => rejectTransportFailure(FileUploadErrorCode.Network, event);
    request.onabort = (event) => {
      if (cancellationSignal?.aborted) {
        cancelRequest();
        return;
      }
      rejectTransportFailure(didStall ? FileUploadErrorCode.RequestTimeout : FileUploadErrorCode.Network, event);
    };
    request.ontimeout = (event) => rejectTransportFailure(FileUploadErrorCode.RequestTimeout, event);
    if (request.upload) {
      request.upload.onprogress = (event) => {
        if (event.loaded > uploadedBytes) {
          uploadedBytes = event.loaded;
          // Do not discard a progressing upload simply to reserve time to
          // retransmit it. The idle watchdog still releases stalled requests,
          // and this timeout remains inside the shared upload deadline.
          request.timeout = Math.max(requestTimeoutMs, progressingRequestTimeoutMs);
          resetStallTimeout();
        }
        try {
          onUploadBytesSent?.(uploadedBytes);
          if (!event.lengthComputable || event.total <= 0) return;
          onUploadProgress?.({
            loadedBytes: event.loaded,
            totalBytes: event.total,
            progressPercent: Math.min(100, Math.max(0, (event.loaded / event.total) * 100)),
          });
        } catch {
          // Progress rendering must never interrupt the upload.
        }
      };
    }
    cancellationSignal?.addEventListener("abort", cancelRequest, { once: true });
    resetStallTimeout();
    request.send(buildPresignedPostFormData(file, presignedPost));
  });
}

export async function uploadFileToPresignedPostThroughCloudflare({
  file,
  presignedPost,
  proxyUrl,
  requestTimeoutMs,
  requestAttemptsBeforeFallback,
  fetchImpl = fetch,
  sleepMs = defaultSleep,
  maxAttempts = 2,
  preferXhrUpload = false,
  xhrUploadImpl = uploadFileToPresignedPostWithXhr,
  onUploadProgress,
  stallTimeoutMs,
  cancellationSignal,
}: {
  file: Blob;
  presignedPost: PresignedPostPayload;
  proxyUrl: string;
  requestTimeoutMs: number;
  requestAttemptsBeforeFallback: number;
  fetchImpl?: FetchImplementation;
  sleepMs?: SleepImplementation;
  maxAttempts?: number;
  preferXhrUpload?: boolean;
  xhrUploadImpl?: StorageUploadFallback;
  onUploadProgress?: (progress: StorageUploadProgress) => void;
  stallTimeoutMs?: number;
  cancellationSignal?: AbortSignal;
}): Promise<StorageUploadResponse> {
  const normalizedMaximumAttempts = Math.max(1, maxAttempts);
  const deadlineMs = Date.now() + Math.max(1, requestTimeoutMs);
  let lastTransportError: unknown;
  let didLastAttemptTimeOut = false;
  let completedProxyAttempts = 0;
  // Progress on the proxy matters for large files too. A stalled connection
  // may still recover through fetch, within the same remaining upload budget.
  if (preferXhrUpload) {
    try {
      const response = await xhrUploadImpl({
        file, presignedPost: { ...presignedPost, url: proxyUrl },
        requestTimeoutMs, requestAttemptsBeforeFallback, cancellationSignal,
        onUploadProgress, stallTimeoutMs,
      });
      return { ...response, transport: "cloudflare-proxy" };
    } catch (error) {
      if (isFileUploadCancellation(error)) throw error;
      const isRecoverableTransportError = error instanceof FileUploadError &&
        [FileUploadErrorCode.Network, FileUploadErrorCode.RequestTimeout].includes(error.code);
      if (!isRecoverableTransportError) throw error;
      completedProxyAttempts = 1;
      lastTransportError = error;
      didLastAttemptTimeOut = error.code === FileUploadErrorCode.RequestTimeout;
    }
  }
  const attemptsBeforeFetch = completedProxyAttempts;

  for (let attemptNumber = 1; attemptNumber <= normalizedMaximumAttempts; attemptNumber += 1) {
    if (cancellationSignal?.aborted) {
      throw createFileUploadCancellationError(FileUploadStage.UploadToStorage, cancellationSignal.reason);
    }
    const remainingTimeMs = deadlineMs - Date.now();
    if (remainingTimeMs <= 0) { didLastAttemptTimeOut = true; break; }
    completedProxyAttempts = attemptsBeforeFetch + attemptNumber;
    const abortController = new AbortController();
    const cancelCurrentRequest = () => abortController.abort(cancellationSignal?.reason);
    cancellationSignal?.addEventListener("abort", cancelCurrentRequest, { once: true });
    const timeoutId = globalThis.setTimeout(() => abortController.abort(), remainingTimeMs);
    try {
      const response = await fetchImpl(proxyUrl, {
        method: "POST",
        // Rebuild FormData for every attempt. A browser may consume the first
        // request body before losing the response, while the stable S3 key makes
        // repeating the same document upload idempotent.
        body: buildPresignedPostFormData(file, presignedPost),
        signal: abortController.signal,
      });
      const responseBodyDetails = await getStorageErrorDetailsFromResponse(response);
      return {
        ok: response.ok,
        status: response.status,
        transport: "cloudflare-proxy",
        requestAttempts: requestAttemptsBeforeFallback + completedProxyAttempts,
        ...responseBodyDetails,
        storageRequestId: getResponseHeader(response, "x-amz-request-id") || responseBodyDetails.storageRequestId,
        storageExtendedRequestId:
          getResponseHeader(response, "x-amz-id-2") || responseBodyDetails.storageExtendedRequestId,
      };
    } catch (error) {
      if (cancellationSignal?.aborted) {
        throw createFileUploadCancellationError(FileUploadStage.UploadToStorage, cancellationSignal.reason || error);
      }
      if (!isTransientNetworkError(error)) throw error;
      lastTransportError = error;
      didLastAttemptTimeOut = abortController.signal.aborted;
      if (didLastAttemptTimeOut || attemptNumber >= normalizedMaximumAttempts) break;
      await sleepMs(Math.min(400 * attemptNumber, Math.max(0, deadlineMs - Date.now())));
    } finally {
      globalThis.clearTimeout(timeoutId);
      cancellationSignal?.removeEventListener("abort", cancelCurrentRequest);
    }
  }

  throw new FileUploadError({
    message: didLastAttemptTimeOut ? "Upload fallback timed out" : "Upload fallback failed",
    stage: FileUploadStage.UploadToStorage,
    code: didLastAttemptTimeOut ? FileUploadErrorCode.RequestTimeout : FileUploadErrorCode.Network,
    retryable: true,
    requestAttempts: requestAttemptsBeforeFallback + completedProxyAttempts,
    storageTransport: "cloudflare-proxy",
    cause: lastTransportError,
  });
}

/**
 * Gate for the storage transport ladder. A file the browser cannot read fails
 * every transport (direct S3, fetch recovery, Cloudflare proxy) identically
 * and immediately, so it must be rejected before any of them run. Readable
 * files continue into the unchanged ladder.
 */
export async function assertLocalFileIsReadable({
  file,
  cancellationSignal,
  maximumProbeBytes = LOCAL_FILE_READ_PROBE_MAX_BYTES,
  timeoutMs = LOCAL_FILE_READ_PROBE_TIMEOUT_MS,
  readBlobBytes,
}: {
  file: Blob;
  cancellationSignal?: AbortSignal;
  maximumProbeBytes?: number;
  timeoutMs?: number;
  readBlobBytes?: ReadBlobBytes;
}): Promise<LocalFileReadabilityResult> {
  if (cancellationSignal?.aborted) {
    throw createFileUploadCancellationError(FileUploadStage.ReadLocalFile, cancellationSignal.reason);
  }
  const readabilityResult = await probeLocalFileReadability({
    file,
    maximumProbeBytes,
    timeoutMs,
    readBlobBytes,
    signal: cancellationSignal,
  });
  if (
    cancellationSignal?.aborted ||
    (readabilityResult.isReadable === false && readabilityResult.failureKind === "aborted")
  ) {
    throw createFileUploadCancellationError(FileUploadStage.ReadLocalFile, cancellationSignal?.reason);
  }
  if (readabilityResult.isReadable === false) {
    const failureMessageByKind: Record<LocalFileReadFailureKind, string> = {
      "read-error": "The selected file could not be read",
      "empty-read": "The selected file returned no bytes",
      timeout: "Reading the selected file timed out",
      aborted: "Reading the selected file was cancelled",
      "size-changed": "The selected file changed on disk while it was being read",
    };
    throw new FileUploadError({
      message: failureMessageByKind[readabilityResult.failureKind],
      stage: FileUploadStage.ReadLocalFile,
      code: FileUploadErrorCode.FileUnreadable,
      retryable: false,
      fileReadFailureKind: readabilityResult.failureKind,
      cause: readabilityResult.cause,
    });
  }
  return readabilityResult;
}

function getDefaultStorageUploadBudgetMs(fileSizeBytes: number): number {
  // A fixed 12 minutes requires >24 Mbps for a 2.2 GB video. Allow 1 MB/s
  // plus one minute for setup/response, retaining the small-file minimum.
  // The 45-minute cap leaves headroom under our one-hour presigned policy;
  // the independent 30-second idle watchdog still stops stalled XHRs.
  return Math.min(45 * 60_000, Math.max(12 * 60_000, Math.ceil(fileSizeBytes / 1_000) + 60_000));
}

export async function uploadFileToPresignedPost({
  file,
  presignedPost,
  fetchImpl = fetch,
  sleepMs = defaultSleep,
  maxAttempts = 3,
  requestTimeoutMs,
  totalRequestBudgetMs = getDefaultStorageUploadBudgetMs(file.size),
  xhrUploadImpl,
  cloudflareProxyUploadImpl,
  preferXhrUpload = false,
  xhrStallTimeoutMs = 30_000,
  fetchRecoveryBudgetMs = 45_000,
  minimumProxyReserveMs = 3 * 60_000,
  cancellationSignal,
  onUploadProgress,
  onStorageTransportRecovery,
}: {
  file: Blob;
  presignedPost: PresignedPostPayload;
  fetchImpl?: FetchImplementation;
  sleepMs?: SleepImplementation;
  maxAttempts?: number;
  requestTimeoutMs?: number;
  totalRequestBudgetMs?: number;
  xhrUploadImpl?: StorageUploadFallback | null;
  cloudflareProxyUploadImpl?: StorageUploadFallback | null;
  preferXhrUpload?: boolean;
  xhrStallTimeoutMs?: number;
  fetchRecoveryBudgetMs?: number;
  minimumProxyReserveMs?: number;
  cancellationSignal?: AbortSignal;
  onUploadProgress?: (progress: StorageUploadProgress) => void;
  onStorageTransportRecovery?: (recovery: StorageTransportRecovery) => void;
}): Promise<void> {
  validatePresignedPostPayload(presignedPost);
  if (cancellationSignal?.aborted) {
    throw createFileUploadCancellationError(FileUploadStage.UploadToStorage, cancellationSignal.reason);
  }

  const perRequestTimeoutMs = requestTimeoutMs ?? 10 * 60_000;
  const progressingRequestTimeoutMs = requestTimeoutMs ?? totalRequestBudgetMs;
  const normalizedTotalRequestBudgetMs = Math.max(1, totalRequestBudgetMs);
  const uploadDeadlineMs = Date.now() + normalizedTotalRequestBudgetMs;
  const canUseCloudflareProxy = Boolean(cloudflareProxyUploadImpl) && file.size <= CLOUDFLARE_PROXY_MAX_FILE_BYTES;
  // Do not cut a progressing direct upload short to reserve time for a
  // fallback that cannot accept its body (or was not supplied by the caller).
  const proxyReserveMs = canUseCloudflareProxy
    ? Math.min(Math.max(1, minimumProxyReserveMs), Math.max(1, Math.floor(normalizedTotalRequestBudgetMs / 3)))
    : 0;
  const normalizedFetchRecoveryBudgetMs = Math.min(
    Math.max(1, fetchRecoveryBudgetMs),
    Math.max(1, Math.floor(normalizedTotalRequestBudgetMs / 3))
  );
  let storageResponse: StorageUploadResponse | null = null;
  let storageRequestAttempts = 0;
  let latestTransportError: FileUploadError | null = null;
  let didUseRecoveryTransport = false;
  const canUseNativeXhr = fetchImpl === fetch && typeof XMLHttpRequest !== "undefined" && xhrUploadImpl !== null;
  const selectedXhrUpload = xhrUploadImpl || (canUseNativeXhr ? uploadFileToPresignedPostWithXhr : null);
  let didAttemptXhr = false;
  // Every transport sends `activeUploadBody`; it is the picked File until a
  // byte-less first failure eligible for recovery replaces it with an in-memory copy.
  let activeUploadBody: Blob = file;
  let bodyMaterialized = false;
  let observedUploadedBytes = 0;
  const trackUploadProgress = (progress: StorageUploadProgress) => {
    observedUploadedBytes = Math.max(observedUploadedBytes, progress.loadedBytes);
    onUploadProgress?.(progress);
  };

  const retainTransportError = (error: unknown): void => {
    if (isFileUploadCancellation(error)) throw error;
    if (
      !(error instanceof FileUploadError) ||
      error.stage !== FileUploadStage.UploadToStorage ||
      ![FileUploadErrorCode.Network, FileUploadErrorCode.RequestTimeout].includes(error.code)
    ) {
      throw error;
    }
    latestTransportError = error;
    storageRequestAttempts = Math.max(storageRequestAttempts, error.requestAttempts);
  };

  const uploadWithXhr = async (availableTimeMs: number): Promise<void> => {
    if (!selectedXhrUpload || availableTimeMs <= 0) return;
    didAttemptXhr = true;
    try {
      const xhrResponse = await selectedXhrUpload({
        file: activeUploadBody,
        presignedPost,
        requestTimeoutMs: Math.min(perRequestTimeoutMs, availableTimeMs),
        progressingRequestTimeoutMs: Math.min(progressingRequestTimeoutMs, uploadDeadlineMs - Date.now()),
        onUploadBytesSent: loadedBytes => { observedUploadedBytes = Math.max(observedUploadedBytes, loadedBytes); },
        requestAttemptsBeforeFallback: storageRequestAttempts,
        cancellationSignal,
        onUploadProgress: trackUploadProgress,
        stallTimeoutMs: xhrStallTimeoutMs,
      });
      if (!xhrResponse) {
        throw new FileUploadError({
          message: "Storage upload did not return a response",
          stage: FileUploadStage.UploadToStorage,
          code: FileUploadErrorCode.InvalidResponse,
          retryable: true,
          requestAttempts: storageRequestAttempts + 1,
          storageTransport: "xhr",
        });
      }
      storageResponse = xhrResponse;
      storageRequestAttempts = xhrResponse.requestAttempts ?? storageRequestAttempts + 1;
    } catch (error) {
      retainTransportError(error);
    }
  };

  if (preferXhrUpload && selectedXhrUpload) {
    const primaryXhrStartedAtMs = Date.now();
    const primaryXhrBudgetMs =
      uploadDeadlineMs - Date.now() - normalizedFetchRecoveryBudgetMs - proxyReserveMs;
    await uploadWithXhr(primaryXhrBudgetMs);

    const shouldRetryWithMaterializedBody =
      !storageResponse &&
      Boolean(latestTransportError) &&
      uploadDeadlineMs > Date.now() &&
      shouldMaterializeUploadBodyAfterFirstTransportFailure({
        fileSizeBytes: file.size,
        elapsedMs: Date.now() - primaryXhrStartedAtMs,
        uploadedBytes: observedUploadedBytes,
        didTimeOut: latestTransportError?.code === FileUploadErrorCode.RequestTimeout,
      });
    if (shouldRetryWithMaterializedBody) {
      try {
        activeUploadBody = await materializeUploadBody({
          file,
          failedTransportError: latestTransportError,
          storageRequestAttempts,
          cancellationSignal,
          timeoutMs: Math.min(LOCAL_FILE_MATERIALIZE_TIMEOUT_MS, uploadDeadlineMs - Date.now()),
        });
      } catch (error) {
        // Running out of the shared upload budget is not evidence of an
        // unreadable file. Preserve the original transport failure in that case.
        const exhaustedUploadBudget = error instanceof FileUploadError &&
          error.fileReadFailureKind === "timeout" && uploadDeadlineMs <= Date.now();
        if (exhaustedUploadBudget) throw latestTransportError;
        throw error;
      }
      bodyMaterialized = true;
      didUseRecoveryTransport = true;
      await uploadWithXhr(uploadDeadlineMs - Date.now() - normalizedFetchRecoveryBudgetMs - proxyReserveMs);
    }
  }

  if (!storageResponse) {
    // The short fetch window reserves time for a usable proxy. Without one,
    // retransmitting a large file needs the remaining direct-upload budget.
    const reserveTimeForProxy = preferXhrUpload && canUseCloudflareProxy;
    const fetchRecoveryDeadlineMs = reserveTimeForProxy
      ? Math.min(uploadDeadlineMs - proxyReserveMs, Date.now() + normalizedFetchRecoveryBudgetMs)
      : uploadDeadlineMs;
    if (fetchRecoveryDeadlineMs > Date.now()) {
      if (didAttemptXhr) didUseRecoveryTransport = true;
      const requestAttemptsBeforeFetch = storageRequestAttempts;
      let fetchRequestAttempts = 0;
      try {
        const response = await fetchWithNetworkRetry({
          url: presignedPost.url,
          requestInit: () => {
            fetchRequestAttempts += 1;
            return { method: "POST", body: buildPresignedPostFormData(activeUploadBody, presignedPost) };
          },
          stage: FileUploadStage.UploadToStorage,
          fetchImpl,
          sleepMs,
          maxAttempts,
          requestTimeoutMs: Math.min(
            perRequestTimeoutMs,
            reserveTimeForProxy ? normalizedFetchRecoveryBudgetMs : normalizedTotalRequestBudgetMs
          ),
          overallDeadlineMs: fetchRecoveryDeadlineMs,
          cancellationSignal,
        });
        const responseBodyDetails = await getStorageErrorDetailsFromResponse(response);
        storageResponse = {
          ok: response.ok,
          status: response.status,
          transport: "fetch",
          ...responseBodyDetails,
          storageRequestId: getResponseHeader(response, "x-amz-request-id") || responseBodyDetails.storageRequestId,
          storageExtendedRequestId:
            getResponseHeader(response, "x-amz-id-2") || responseBodyDetails.storageExtendedRequestId,
        };
        storageRequestAttempts = requestAttemptsBeforeFetch + fetchRequestAttempts;
      } catch (error) {
        if (error instanceof FileUploadError && !isFileUploadCancellation(error)) {
          retainTransportError(
            new FileUploadError({
              message: error.message,
              stage: error.stage,
              code: error.code,
              retryable: error.retryable,
              requestAttempts: requestAttemptsBeforeFetch + error.requestAttempts,
              httpStatus: error.httpStatus,
              cfRayId: error.cfRayId,
              storageRequestId: error.storageRequestId,
              storageExtendedRequestId: error.storageExtendedRequestId,
              storageTransport: error.storageTransport,
              storageErrorCode: error.storageErrorCode,
              storageErrorMessage: error.storageErrorMessage,
              cause: error.cause,
            })
          );
        } else {
          retainTransportError(error);
        }
      }
    }
  }

  if (!storageResponse && !didAttemptXhr && selectedXhrUpload) {
    didUseRecoveryTransport = true;
    await uploadWithXhr(uploadDeadlineMs - Date.now());
  }

  if (!storageResponse && canUseCloudflareProxy && cloudflareProxyUploadImpl) {
    const remainingUploadTimeMs = uploadDeadlineMs - Date.now();
    if (remainingUploadTimeMs > 0) {
      didUseRecoveryTransport = true;
      try {
        storageResponse = await cloudflareProxyUploadImpl({
          file: activeUploadBody,
          presignedPost,
          requestTimeoutMs: Math.min(perRequestTimeoutMs, remainingUploadTimeMs),
          requestAttemptsBeforeFallback: storageRequestAttempts,
          cancellationSignal,
          onUploadProgress: trackUploadProgress,
          stallTimeoutMs: xhrStallTimeoutMs,
        });
        storageRequestAttempts = storageResponse.requestAttempts ?? storageRequestAttempts + 1;
      } catch (error) {
        retainTransportError(error);
      }
    }
  }

  if (!storageResponse && latestTransportError) {
    throw bodyMaterialized ? cloneFileUploadError(latestTransportError, { bodyMaterialized: true }) : latestTransportError;
  }

  if (!storageResponse) {
    throw new FileUploadError({
      message: "Storage upload did not return a response",
      stage: FileUploadStage.UploadToStorage,
      code: FileUploadErrorCode.InvalidResponse,
      retryable: true,
      requestAttempts: storageRequestAttempts,
      bodyMaterialized,
    });
  }

  // A picked-file multipart submission can lack a usable file part even
  // though the readability probe passed. This exact rejection
  // confirms no object was accepted. Retry once with verified in-memory bytes;
  // other InvalidArgument errors, authorization failures and large files retain
  // their ordinary terminal behavior. Do not restart the transport ladder.
  const shouldRecoverRejectedFilePart =
    !storageResponse.ok &&
    storageResponse.status === 400 &&
    storageResponse.storageErrorCode === "InvalidArgument" &&
    storageResponse.storageErrorMessage === "POST requires exactly one file upload per request." &&
    !bodyMaterialized && file.size > 0 && file.size <= S3_FILE_PART_RECOVERY_MAX_BYTES &&
    uploadDeadlineMs > Date.now();
  if (shouldRecoverRejectedFilePart) {
    try {
      activeUploadBody = await materializeUploadBody({
        file,
        failedTransportError: new FileUploadError({
          message: "Storage rejected the multipart file part",
          stage: FileUploadStage.UploadToStorage,
          code: FileUploadErrorCode.Http,
          retryable: false,
          storageTransport: storageResponse.transport,
        }),
        storageRequestAttempts,
        cancellationSignal,
        timeoutMs: Math.min(LOCAL_FILE_MATERIALIZE_TIMEOUT_MS, uploadDeadlineMs - Date.now()),
      });
    } catch (error) {
      const sharedDeadlineExpiredDuringRead = error instanceof FileUploadError &&
        error.code === FileUploadErrorCode.FileUnreadable && error.fileReadFailureKind === "timeout" &&
        uploadDeadlineMs <= Date.now();
      // Exhausting the upload budget does not prove that the file is unreadable.
      // Keep the original storage rejection when no recovery request could start.
      if (!sharedDeadlineExpiredDuringRead) throw error;
    }
    if (uploadDeadlineMs > Date.now()) {
      bodyMaterialized = true;
      didUseRecoveryTransport = true;
      const requestAttemptsBeforeRecovery = storageRequestAttempts;
      try {
        const response = await fetchWithNetworkRetry({
          url: presignedPost.url,
          requestInit: () => {
            storageRequestAttempts += 1;
            return { method: "POST", body: buildPresignedPostFormData(activeUploadBody, presignedPost) };
          },
          stage: FileUploadStage.UploadToStorage,
          fetchImpl,
          sleepMs,
          maxAttempts: 1,
          requestTimeoutMs: Math.min(perRequestTimeoutMs, uploadDeadlineMs - Date.now()),
          overallDeadlineMs: uploadDeadlineMs,
          cancellationSignal,
        });
        const details = await getStorageErrorDetailsFromResponse(response);
        storageResponse = {
          ok: response.ok,
          status: response.status,
          transport: "fetch",
          ...details,
          storageRequestId: getResponseHeader(response, "x-amz-request-id") || details.storageRequestId,
          storageExtendedRequestId: getResponseHeader(response, "x-amz-id-2") || details.storageExtendedRequestId,
        };
      } catch (error) {
        if (error instanceof FileUploadError && !isFileUploadCancellation(error)) {
          throw cloneFileUploadError(error, {
            requestAttempts: requestAttemptsBeforeRecovery + error.requestAttempts,
            storageTransport: "fetch",
            bodyMaterialized: true,
          });
        }
        throw error;
      }
    }
  }

  // A transient S3 rejection can arrive on any transport. Reuse the signed
  // object key and verified body, without restarting the fallback ladder or
  // granting a new deadline/attempt budget.
  const isTransientStorageResponse = (response: StorageUploadResponse) =>
    !response.ok && ([408, 429, 500, 502, 503, 504].includes(response.status) ||
      (response.status === 400 && response.storageErrorCode === "RequestTimeout"));
  const storageRetryRequest = () => {
    storageRequestAttempts += 1;
    return { method: "POST", body: buildPresignedPostFormData(activeUploadBody, presignedPost) };
  };
  while (isTransientStorageResponse(storageResponse) && storageRequestAttempts < maxAttempts) {
    const remainingMs = uploadDeadlineMs - Date.now();
    if (remainingMs <= 0) break;
    await sleepMs(Math.min(400 * storageRequestAttempts, remainingMs));
    if (cancellationSignal?.aborted) {
      throw createFileUploadCancellationError(FileUploadStage.UploadToStorage, cancellationSignal.reason);
    }
    if (uploadDeadlineMs <= Date.now()) break;
    didUseRecoveryTransport = true;
    const attemptsBeforeRetry = storageRequestAttempts;
    try {
      const response = await fetchWithNetworkRetry({
        url: presignedPost.url,
        requestInit: storageRetryRequest,
        stage: FileUploadStage.UploadToStorage,
        fetchImpl,
        sleepMs,
        maxAttempts: 1,
        requestTimeoutMs: perRequestTimeoutMs,
        overallDeadlineMs: uploadDeadlineMs,
        cancellationSignal,
      });
      const details = await getStorageErrorDetailsFromResponse(response);
      storageResponse = {
        ok: response.ok,
        status: response.status,
        transport: "fetch",
        ...details,
        storageRequestId: getResponseHeader(response, "x-amz-request-id") || details.storageRequestId,
        storageExtendedRequestId: getResponseHeader(response, "x-amz-id-2") || details.storageExtendedRequestId,
      };
    } catch (error) {
      if (error instanceof FileUploadError && !isFileUploadCancellation(error)) {
        throw cloneFileUploadError(error, {
          requestAttempts: attemptsBeforeRetry + error.requestAttempts,
          storageTransport: "fetch",
          bodyMaterialized,
        });
      }
      throw error;
    }
  }

  if (storageResponse.ok && didUseRecoveryTransport) {
    try {
      onStorageTransportRecovery?.({
        transport: storageResponse.transport,
        requestAttemptsBeforeFallback: Math.max(0, storageRequestAttempts - 1),
        storageRequestId: storageResponse.storageRequestId,
        storageExtendedRequestId: storageResponse.storageExtendedRequestId,
        bodyMaterialized,
      });
    } catch {
      // Recovery diagnostics must never turn a successful upload into a failure.
    }
  }

  if (!storageResponse.ok) {
    throw new FileUploadError({
      message: "File storage rejected the upload",
      stage: FileUploadStage.UploadToStorage,
      code: FileUploadErrorCode.Http,
      retryable: isTransientStorageResponse(storageResponse),
      requestAttempts: storageRequestAttempts,
      httpStatus: storageResponse.status,
      storageRequestId: storageResponse.storageRequestId,
      storageExtendedRequestId: storageResponse.storageExtendedRequestId,
      storageTransport: storageResponse.transport,
      storageErrorCode: storageResponse.storageErrorCode,
      storageErrorMessage: storageResponse.storageErrorMessage,
      bodyMaterialized,
    });
  }
}

export async function requestFileProcessingTask({
  url,
  body,
  requestedTaskId,
  idToken,
  fetchImpl = fetch,
  requestTimeoutMs = 30_000,
  cancellationSignal,
}: {
  url: string;
  body: Record<string, unknown>;
  requestedTaskId: string;
  idToken: string | null;
  fetchImpl?: FetchImplementation;
  requestTimeoutMs?: number;
  cancellationSignal?: AbortSignal;
}): Promise<string> {
  if (cancellationSignal?.aborted) {
    throw createFileUploadCancellationError(FileUploadStage.CreateProcessingTask, cancellationSignal.reason);
  }
  const abortController = new AbortController();
  const cancelCurrentRequest = () => abortController.abort(cancellationSignal?.reason);
  cancellationSignal?.addEventListener("abort", cancelCurrentRequest, { once: true });
  const timeoutId = globalThis.setTimeout(() => abortController.abort(), requestTimeoutMs);
  const requestHeaders: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (idToken) {
    requestHeaders.Authorization = `Bearer ${idToken}`;
  }
  const requestBody = { ...body };
  const capability = getUnexpiredUploadCapability(String(body.uploaded_file_s3_object_key));
  if (capability) {
    requestBody.upload_proof ??= capability.proof;
    requestBody.upload_claim_token ??= capability.claim;
  }
  if (!idToken) {
    // Anonymous uploads are supported, but an unverified request must not
    // claim ownership using a stale frontend user object.
    delete requestBody.user_id;
    // Save before sending: the task may be accepted even if its response is lost.
    rememberAnonymousUpload(requestedTaskId, url, requestBody);
  }
  let response: Response;
  try {
    response = await fetchImpl(url, {
      method: "POST",
      headers: requestHeaders,
      body: JSON.stringify({
        ...requestBody,
        ingestion_attempt_id: body.ingestion_attempt_id || requestedTaskId,
        task_id: requestedTaskId,
      }),
      signal: abortController.signal,
    });
  } catch (error) {
    if (cancellationSignal?.aborted) {
      throw createFileUploadCancellationError(FileUploadStage.CreateProcessingTask, cancellationSignal.reason || error);
    }
    if (!isTransientNetworkError(error)) {
      throw error;
    }
    throw new FileUploadError({
      message: abortController.signal.aborted
        ? "Document-processing request timed out"
        : error instanceof Error
          ? error.message
          : "Network request failed",
      stage: FileUploadStage.CreateProcessingTask,
      code: abortController.signal.aborted ? FileUploadErrorCode.RequestTimeout : FileUploadErrorCode.Network,
      retryable: true,
      cause: error,
    });
  } finally {
    globalThis.clearTimeout(timeoutId);
    cancellationSignal?.removeEventListener("abort", cancelCurrentRequest);
  }

  const responseBody = await parseResponseBody(response);
  if (!response.ok) {
    throw new FileUploadError({
      message: "The document-processing service rejected the request",
      stage: FileUploadStage.CreateProcessingTask,
      code: FileUploadErrorCode.Http,
      retryable: response.status >= 500,
      httpStatus: response.status,
      cfRayId: getResponseHeader(response, "cf-ray"),
    });
  }

  const returnedTaskId =
    responseBody && typeof responseBody === "object" ? (responseBody as Record<string, unknown>).task_id : null;
  if (typeof returnedTaskId !== "string" || !returnedTaskId) {
    throw new FileUploadError({
      message: "Document-processing response did not include a task ID",
      stage: FileUploadStage.CreateProcessingTask,
      code: FileUploadErrorCode.InvalidResponse,
      retryable: true,
    });
  }

  return returnedTaskId;
}

export async function requestUploadedFileDownloadUrl({
  url,
  taskId,
  deckId,
  idToken,
  ownerUserId,
  fetchImpl = fetch,
  sleepMs = defaultSleep,
  maxAttempts = 3,
  requestTimeoutMs = 30_000,
  onRetry,
}: {
  url: string;
  taskId: string;
  deckId?: string | null;
  idToken: string;
  ownerUserId: string;
  fetchImpl?: FetchImplementation;
  sleepMs?: SleepImplementation;
  maxAttempts?: number;
  requestTimeoutMs?: number;
  onRetry?: (retry: FileRequestRetry) => void;
}): Promise<string> {
  // Scope before the request starts so a response from a prior signed-in user
  // cannot overwrite a newer user's cache if the requests finish out of order.
  scopeUploadedFileDownloadUrlCache(ownerUserId);
  let attemptedAnonymousClaim = false;
  const normalizedMaximumAttempts = Math.max(1, maxAttempts);
  for (let attemptNumber = 1; attemptNumber <= normalizedMaximumAttempts; attemptNumber += 1) {
    let response: Response;
    try {
      response = await fetchWithNetworkRetry({
        url,
        requestInit: {
          method: "POST",
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            task_id: taskId,
            ...(deckId ? { deck_id: deckId } : {}),
          }),
        },
        stage: FileUploadStage.RetrieveSource,
        fetchImpl,
        sleepMs,
        maxAttempts: 1,
        requestTimeoutMs,
      });
    } catch (error) {
      const transportError =
        error instanceof FileUploadError
          ? new FileUploadError({
              message: error.message,
              stage: error.stage,
              code: error.code,
              retryable: error.retryable,
              requestAttempts: attemptNumber,
              httpStatus: error.httpStatus,
              cfRayId: error.cfRayId,
              responseContentType: error.responseContentType,
              responseBodyType: error.responseBodyType,
              responseBodyKeys: error.responseBodyKeys,
              cause: error.cause,
            })
          : error;
      const canRetry =
        transportError instanceof FileUploadError &&
        transportError.retryable &&
        attemptNumber < normalizedMaximumAttempts;
      if (!canRetry) throw transportError;
      try {
        onRetry?.({
          error: transportError,
          failedAttemptNumber: attemptNumber,
          maximumAttempts: normalizedMaximumAttempts,
          retryReason: "transport",
        });
      } catch {
        // Diagnostics must never prevent the recovery request.
      }
      await sleepMs(400 * attemptNumber);
      continue;
    }

    if (response.status === 404 && !attemptedAnonymousClaim && idToken && ownerUserId) {
      attemptedAnonymousClaim = true;
      // Derive from the caller's trusted API URL, never a storage-provided host.
      const processingUrl = new URL("create_task_to_get_file_data", url).href;
      const claim = getAnonymousUploadClaim(taskId, processingUrl);
      if (claim) {
        const claimedTaskId = await requestFileProcessingTask({
          url: processingUrl,
          requestedTaskId: taskId,
          body: {
            file_name: claim.body.file_name,
            uploaded_file_s3_object_key: claim.body.uploaded_file_s3_object_key,
            content_medium_type: claim.body.content_medium_type,
            ...(claim.body.upload_claim_token ? { upload_claim_token: claim.body.upload_claim_token } : {}),
            claim_existing_task_only: true,
          },
          idToken,
          fetchImpl,
          requestTimeoutMs,
        });
        if (claimedTaskId !== taskId) {
          throw new FileUploadError({
            message: "The upload ownership response did not match the requested task",
            stage: FileUploadStage.RetrieveSource,
            code: FileUploadErrorCode.InvalidResponse,
            retryable: false,
          });
        }
        forgetAnonymousUpload(taskId);
        // Ownership recovery is separate from the transient source-read budget.
        attemptNumber -= 1;
        continue;
      }
    }
    const responseBody = await parseResponseBody(response);
    const downloadUrl =
      responseBody && typeof responseBody === "object" ? (responseBody as Record<string, unknown>).download_url : null;
    if (response.ok && isHttpsUrl(downloadUrl)) {
      const expiresInSeconds =
        responseBody && typeof responseBody === "object"
          ? Number((responseBody as Record<string, unknown>).expires_in_seconds)
          : 0;
      const usableExpiresInSeconds = Number.isFinite(expiresInSeconds) && expiresInSeconds > 0 ? expiresInSeconds : 0;
      if (uploadedFileDownloadUrlCacheOwnerId === ownerUserId) {
        uploadedFileDownloadUrlCache.set(taskId, {
          url: downloadUrl,
          // Refresh slightly before S3 rejects the signed URL.
          expiresAt: Date.now() + Math.max(0, usableExpiresInSeconds - 60) * 1_000,
        });
      }
      return downloadUrl;
    }

    const shouldRetryResponse =
      response.ok || response.status === 408 || response.status === 429 || response.status >= 500;
    const responseBodyShape = getResponseBodyShape(responseBody);
    const responseError = new FileUploadError({
      message: "The uploaded source is temporarily unavailable",
      stage: FileUploadStage.RetrieveSource,
      code: response.ok ? FileUploadErrorCode.InvalidResponse : FileUploadErrorCode.Http,
      retryable: shouldRetryResponse,
      requestAttempts: attemptNumber,
      httpStatus: response.status,
      cfRayId: getResponseHeader(response, "cf-ray"),
      responseContentType: getResponseHeader(response, "content-type"),
      ...responseBodyShape,
    });
    if (!shouldRetryResponse || attemptNumber >= normalizedMaximumAttempts) {
      throw responseError;
    }
    try {
      onRetry?.({
        error: responseError,
        failedAttemptNumber: attemptNumber,
        maximumAttempts: normalizedMaximumAttempts,
        retryReason: "response",
      });
    } catch {
      // Diagnostics must never prevent the recovery request.
    }
    await sleepMs(400 * attemptNumber);
  }

  throw new FileUploadError({
    message: "The uploaded source is temporarily unavailable",
    stage: FileUploadStage.RetrieveSource,
    code: FileUploadErrorCode.InvalidResponse,
    retryable: true,
    requestAttempts: normalizedMaximumAttempts,
  });
}

export function isRecoverableProcessingResponseError(error: unknown): error is FileUploadError {
  return error instanceof FileUploadError &&
    error.stage === FileUploadStage.CreateProcessingTask &&
    error.retryable &&
    [FileUploadErrorCode.Network, FileUploadErrorCode.RequestTimeout, FileUploadErrorCode.InvalidResponse].includes(error.code);
}

export async function createAndWaitForFileProcessing<T>({
  requestedTaskId,
  requestTask,
  waitForTask,
}: {
  requestedTaskId: string;
  requestTask: () => Promise<string>;
  waitForTask: (
    taskId: string,
    options?: { allowMissingDocumentGracePeriod: boolean; cancellationSignal?: AbortSignal }
  ) => Promise<T>;
}): Promise<T> {
  // A missing task after the lookup grace period means the request may never
  // have reached the API. Retry once with the caller's same UUID; the backend
  // creates it atomically and verifies source/owner identity on duplicate calls.
  for (let attempt = 0; ; attempt += 1) {
    try {
      const returnedTaskId = await requestTask();
      return await waitForTask(returnedTaskId);
    } catch (error) {
      // Lost or incomplete success responses warrant looking up the browser's UUID.
      const mayHaveLostCreateResponse = isRecoverableProcessingResponseError(error);
      if (!mayHaveLostCreateResponse) throw error;
      try {
        return await waitForTask(requestedTaskId, { allowMissingDocumentGracePeriod: true });
      } catch (taskLookupError) {
        const taskWasMissing = taskLookupError instanceof FileUploadError &&
          taskLookupError.code === FileUploadErrorCode.MissingTask;
        // A failed lookup cannot prove absence; retry only a confirmed miss once.
        const canRetryMissingCreate = taskWasMissing && attempt === 0 && !taskLookupError.cause;
        if (canRetryMissingCreate) continue;
        if (taskLookupError instanceof FileUploadError && !taskWasMissing) throw taskLookupError;
        throw error;
      }
    }
  }
}

function getUploadFailureCauseTelemetry(cause: unknown): {
  causeName: string | null;
  causeMessage: string | null;
  causeCode: string | null;
} {
  if (!cause || (typeof cause !== "object" && typeof cause !== "function")) {
    return {
      causeName: null,
      causeMessage: typeof cause === "string" ? cause : null,
      causeCode: null,
    };
  }
  const causeRecord = cause as {
    name?: unknown;
    message?: unknown;
    code?: unknown;
  };
  return {
    causeName: typeof causeRecord.name === "string" ? causeRecord.name : null,
    causeMessage: typeof causeRecord.message === "string" ? causeRecord.message : null,
    causeCode:
      typeof causeRecord.code === "string" || typeof causeRecord.code === "number" ? String(causeRecord.code) : null,
  };
}

const CORRUPTED_FILE_MESSAGE =
  "we couldn’t read this file. save a new copy and try again.";

export function getFileProcessingErrorMessage(
  processingData: Record<string, unknown>,
  sourceNameOrPageUrl: string | null,
  translate: FileUploadTranslate = useFallbackTranslation
): string {
  const errorCode =
    typeof processingData.file_processing_error_code === "string" ? processingData.file_processing_error_code : "";
  const processingMessage = typeof processingData.message === "string" ? processingData.message : "";
  const normalizedSourceName = sourceNameOrPageUrl?.toLowerCase().split(/[?#]/, 1)[0] || "";
  const isHeicOrHeifSource = normalizedSourceName.endsWith(".heic") || normalizedSourceName.endsWith(".heif");

  const specificMessages: Record<string, [string, string]> = {
    image_dimensions_too_large: ["document_upload_error_image_dimensions", "this image is too large to read. resize it and try again."],
    source_download_failed: ["document_upload_error_download_failed", "the download didn’t include your file. download it again from the original site."],
    not_pdf_header: ["document_upload_error_not_pdf", "this file isn’t a PDF. download or export a new PDF and try again."],
    file_empty: ["document_upload_error_empty", "this file is empty. download a new copy and try again."],
    text_file_unreadable: ["document_upload_error_text_unreadable", "we couldn’t read this file as text. save it as UTF-8 text and try again."],
    text_file_too_large: ["document_upload_error_text_too_large", "this text file is too large. split it into smaller files and try again."],
  };
  const specificMessage = Object.prototype.hasOwnProperty.call(specificMessages, errorCode) ? specificMessages[errorCode] : undefined;
  if (specificMessage) return translate(...specificMessage);
  if (errorCode === "ppt_locked" || errorCode === "pdf_encrypted" || errorCode === "pdf_locked") {
    return translate(
      "document_upload_error_password_protected",
      "this file is password-protected. remove the password or export an unlocked copy, then try again."
    );
  }
  if (processingMessage.toLowerCase() === "no text found in the image" || errorCode === "no_text_found") {
    return translate(
      "document_upload_error_no_text",
      "no text was found in this file. try a clearer image or a document with selectable text."
    );
  }
  if (
    isHeicOrHeifSource &&
    (!errorCode ||
      errorCode === "file_corrupted" ||
      errorCode === "image_decode_failed" ||
      /decode|empty image|unsupported image|cannot identify image/.test(processingMessage.toLowerCase()))
  ) {
    return translate(
      "document_upload_error_heic_unreadable",
      "we couldn’t read this HEIC image. save it as JPG or PNG and try again."
    );
  }
  if (errorCode === "image_decode_failed") {
    return translate("document_upload_error_image_unreadable", "we couldn’t read this image. save a new JPG or PNG copy and try again.");
  }
  if (
    errorCode === "file_corrupted" || errorCode === "pdf_trailer_missing"
  ) {
    return translate("document_upload_error_unreadable", CORRUPTED_FILE_MESSAGE);
  }
  return translate(
    "document_upload_error_generic",
    "we couldn’t process this source. please try again."
  );
}

export function getFileUploadUserMessage(
  error: unknown,
  isOnline = typeof navigator === "undefined" ? true : navigator.onLine,
  translate: FileUploadTranslate = useFallbackTranslation
): string {
  if (!(error instanceof FileUploadError)) {
    return translate(
      "document_upload_error_generic",
      "we couldn’t process this source. please try again."
    );
  }
  if (error.code === FileUploadErrorCode.SourceUnavailable) {
    return translate("question_generation_error.source_unavailable", "we couldn't access this document. reopen it or upload it again.");
  }
  if (error.code === FileUploadErrorCode.FileUnreadable) {
    return translate(
      "document_upload_error_file_unreadable",
      "we couldn’t read the file you selected from your device. choose it again from your device’s Files app, or upload it from a computer."
    );
  }
  if (error.code === FileUploadErrorCode.Network && !isOnline) {
    return translate(
      "document_upload_error_offline",
      "your internet connection was interrupted. reconnect, then try uploading the file again."
    );
  }
  if (error.code === FileUploadErrorCode.Network) {
    return translate(
      "document_upload_error_connection",
      "the connection was interrupted while uploading your file. please try again; switching networks or browsers may help."
    );
  }
  if (error.code === FileUploadErrorCode.RequestTimeout) {
    return translate(
      "document_upload_error_request_timeout",
      "the upload request took too long. please try again; switching networks or browsers may help."
    );
  }
  if (error.storageErrorCode === "EntityTooLarge") {
    return translate(
      "document_upload_error_file_too_large",
      "this file is too large for the upload service. try a smaller file or split it into multiple documents."
    );
  }
  if (error.code === FileUploadErrorCode.Http && error.httpStatus && error.httpStatus >= 500) {
    return translate(
      "document_upload_error_service_unavailable",
      "the document service is temporarily unavailable. please wait a moment and try again."
    );
  }
  if (error.stage === FileUploadStage.UploadToStorage && error.code === FileUploadErrorCode.Http) {
    return translate(
      "document_upload_error_storage_rejected",
      "the file storage service rejected this upload. please try again."
    );
  }
  if (error.code === FileUploadErrorCode.RestorationRequired) {
    return translate(
      "document_upload_error_restoration",
      "this file is being restored. try again in 48 hours, or upload a new copy."
    );
  }
  if (error.code === FileUploadErrorCode.ProcessingTimeout) {
    return translate(
      "document_upload_error_timeout",
      "this file is taking longer than expected to process. please try again."
    );
  }
  if (error.code === FileUploadErrorCode.EmptyFile) {
    return translate("document_upload_error_unreadable", CORRUPTED_FILE_MESSAGE);
  }
  return translate(
    "document_upload_error_generic",
    "we couldn’t process this source. please try again."
  );
}

export function getSafeFileMetadata(file: Pick<File, "name" | "type" | "size">) {
  const finalNameSegment = file.name.split("/").pop() || "";
  const fileExtension = finalNameSegment.includes(".") ? finalNameSegment.split(".").pop()?.toLowerCase() || "" : "";
  return {
    fileName: finalNameSegment,
    fileExtension,
    fileMimeType: file.type || "unknown",
    fileSizeBytes: file.size,
    isEmpty: file.size === 0,
  };
}

export function isSupportedUploadFile(file: Pick<File, "name" | "type">, acceptedFileTypes: string): boolean {
  const acceptedTokens = acceptedFileTypes
    .split(",")
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean);
  const fileMimeType = file.type.toLowerCase();
  const fileExtension = file.name.includes(".") ? `.${file.name.split(".").pop()?.toLowerCase()}` : "";

  return Boolean(
    (fileMimeType && acceptedTokens.includes(fileMimeType)) || (fileExtension && acceptedTokens.includes(fileExtension))
  );
}

export function getUnsupportedUploadFileType(file: Pick<File, "name" | "type">): string {
  const finalNameSegment = file.name.split("/").pop() || "";
  const fileExtension = finalNameSegment.includes(".")
    ? finalNameSegment.split(".").pop()?.toLowerCase() || ""
    : "";

  return fileExtension || file.type.toLowerCase() || "unknown";
}

export function getUnsupportedFileUploadMessage(
  file: Pick<File, "name" | "type">,
  translate: FileUploadTranslate = useFallbackTranslation
): string {
  const unsupportedFileType = getUnsupportedUploadFileType(file);
  if (unsupportedFileType === "unknown") {
    return translate(
      "document_upload_error_missing_extension",
      "we do not support uploading files without a file extension. try again with a different file type."
    );
  }

  const displayedFileType = file.name.includes(".") ? `.${unsupportedFileType}` : unsupportedFileType;
  const translatedMessage = translate(
    "document_upload_error_unsupported_file_type",
    "we do not support uploading {fileType} files. try again with a different file type.",
    { fileType: displayedFileType }
  );
  return translatedMessage.replace("{fileType}", displayedFileType);
}

export function containsDroppedFolder(
  droppedItems: ArrayLike<{
    kind: string;
    webkitGetAsEntry?: () => { isDirectory?: boolean } | null;
  }>
): boolean {
  return Array.from(droppedItems).some(
    (droppedItem) => droppedItem.kind === "file" && droppedItem.webkitGetAsEntry?.()?.isDirectory === true
  );
}

export function validateSelectedFileCount(files: File[], maximumFileCount = 5): FileUploadError | null {
  if (files.length <= maximumFileCount) {
    return null;
  }
  return new FileUploadError({
    message: `A maximum of ${maximumFileCount} files can be uploaded at once`,
    stage: FileUploadStage.ValidateInput,
    code: FileUploadErrorCode.TooManyFiles,
    retryable: false,
  });
}

export function areAnyFilesBeingRead(
  files: Array<{ isBeingRead?: boolean; [key: string]: unknown }> | null | undefined
): boolean {
  return Array.isArray(files) && files.some((file) => file.isBeingRead === true);
}

/**
 * Processing status documents from older or partially recovered PDF tasks can
 * contain a zero page count even after their per-page text has been written.
 * Keep the page range usable when that happens by using the text collection as
 * the bounded fallback; a reported positive count always remains authoritative.
 */
export function getEffectivePdfPageCount(pdfNumPages: unknown, pdfPagesTextArray: unknown): number {
  const reportedPageCount =
    typeof pdfNumPages === "number" && Number.isFinite(pdfNumPages) ? Math.floor(pdfNumPages) : 0;
  const textPageCount = Array.isArray(pdfPagesTextArray) ? pdfPagesTextArray.length : 0;
  return reportedPageCount > 0 ? reportedPageCount : textPageCount;
}

export function markFileUploadBatchAsInactive<T extends { isBeingRead?: boolean }>(files: T[]): T[] {
  files.forEach((file) => {
    file.isBeingRead = false;
  });
  return [...files];
}

export function getFileUploadBatchRecoveryAvailability(
  fileCount: number,
  hasStorageUploadCompleted: boolean
): { canCancel: boolean; canRetry: boolean } {
  const canCancel = fileCount > 0 && !hasStorageUploadCompleted;
  return {
    canCancel,
    // Retrying a multi-file batch after one file advances can duplicate work
    // for files whose backend task is already running. Keep retry single-file.
    canRetry: canCancel && fileCount === 1,
  };
}

export function markFileUploadAsFailed(
  fileUploadState: {
    isBeingRead?: boolean;
    hasUploadError?: boolean;
    uploadErrorMessage?: string | null;
  },
  uploadErrorMessage: string
): void {
  fileUploadState.isBeingRead = false;
  fileUploadState.hasUploadError = true;
  fileUploadState.uploadErrorMessage = uploadErrorMessage;
}

export function buildUploadFailureTelemetry({
  error,
  attemptId,
  inputMethod,
  file,
  contentMediumType,
  stableUploadedFileIdentifiers,
}: {
  error: FileUploadError;
  attemptId: string;
  inputMethod: FileUploadInputMethod;
  file: Pick<File, "name" | "type" | "size">;
  contentMediumType: string | null;
  stableUploadedFileIdentifiers?: StableUploadedFileIdentifiers;
}): Record<string, string | number | boolean | null> {
  const fileMetadata = getSafeFileMetadata(file);
  const causeTelemetry = getUploadFailureCauseTelemetry(error.cause);
  const storageUploadConfirmed = [
    FileUploadStage.CreateProcessingTask,
    FileUploadStage.AwaitProcessing,
    FileUploadStage.ReadProcessedText,
  ].includes(error.stage);
  const jungleBackendRequestStarted = storageUploadConfirmed;
  return {
    outcome: "failed",
    stage: error.stage,
    errorCode: error.code,
    retryable: error.retryable,
    requestAttempts: error.requestAttempts,
    attemptId,
    inputMethod,
    fileName: fileMetadata.fileName,
    fileExtension: fileMetadata.fileExtension,
    fileMimeType: fileMetadata.fileMimeType,
    fileSizeBytes: fileMetadata.fileSizeBytes,
    contentMediumType,
    uploadedFileBucketName: stableUploadedFileIdentifiers?.bucketName || null,
    uploadedFileObjectKey: stableUploadedFileIdentifiers?.objectKey || null,
    fileProcessingTaskId: stableUploadedFileIdentifiers?.taskId || attemptId,
    modalFunctionCallId: stableUploadedFileIdentifiers?.modalFunctionCallId || null,
    uploadedFileLocator:
      stableUploadedFileIdentifiers?.bucketName && stableUploadedFileIdentifiers?.objectKey
        ? `s3://${stableUploadedFileIdentifiers.bucketName}/${stableUploadedFileIdentifiers.objectKey}`
        : null,
    storageUploadConfirmed,
    jungleBackendRequestStarted,
    ...(error.httpStatus === undefined ? {} : { httpStatus: error.httpStatus }),
    cfRayId: error.cfRayId || null,
    storageRequestId: error.storageRequestId || null,
    storageExtendedRequestId: error.storageExtendedRequestId || null,
    storageTransport: error.storageTransport || (error.stage === FileUploadStage.UploadToStorage ? "fetch" : null),
    bodyMaterialized: error.bodyMaterialized,
    storageErrorCode: error.storageErrorCode || null,
    storageErrorMessage: error.storageErrorMessage || null,
    ...(error.fileReadFailureKind ? { fileReadFailureKind: error.fileReadFailureKind } : {}),
    ...(causeTelemetry.causeName || causeTelemetry.causeMessage || causeTelemetry.causeCode ? causeTelemetry : {}),
  };
}
